import { useMemo } from 'react';
import clsx from 'clsx';

import { Task } from '@atom/types/task';
import { TaskLocation } from '@atom/types/taskLocation';
import { WorkOrderType, WorkReport } from '@atom/types/work';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import CustomFieldsSection from './CustomFieldsSection';
import ReportAssetsSection from './ReportAssetsSection';
import { EMPTY_TASK } from './reportConstants';
import ReportLocationsSection from './ReportLocationsSection';
import ReportMaterials from './ReportMaterials';
import ReportMultiAssetsSection from './ReportMultiAssetsSection';
import ReportSection from './ReportSection';
import ReportUsers from './ReportUsers';
import {
  getFilteredMaterials,
  getFilteredUsers,
  getNotesField,
  getSectionActualCost,
  ReportTaskMaterials,
  ReportUser,
} from './reportUtilities';

import classes from './workOrderReport.module.css';

interface Props {
  task: Task;
  type: WorkOrderType;
  isCompletedOrAbove: boolean;
  reportUsers: ReportUser[];
  reportMaterials: ReportTaskMaterials;
  reportAssets: any[];
  aldotWorkReport: WorkReport;
  reportLocations: TaskLocation[];
  lastTask: boolean;
}

const TaskDetailReport = ({
  task,
  type,
  reportUsers,
  reportMaterials,
  reportAssets,
  isCompletedOrAbove,
  aldotWorkReport,
  reportLocations,
  lastTask,
}: Props) => {
  const filteredUsers = getFilteredUsers(reportUsers, isCompletedOrAbove);

  const filteredStartEndMaterials = getFilteredMaterials(
    reportMaterials?.startEndMaterials,
    isCompletedOrAbove,
  );

  const filteredUsageMaterials = getFilteredMaterials(
    reportMaterials?.usageMaterials,
    isCompletedOrAbove,
  );

  const actualCostData = getSectionActualCost(task);

  const showCustomFields =
    !isNilOrEmpty(task?.fields) || task?.id === EMPTY_TASK.id;

  const hideTaskName = task?.id === EMPTY_TASK.id;
  const showTitleSubtext = isCompletedOrAbove && aldotWorkReport;

  const titleSubtext = useMemo(() => {
    if (isNilOrEmpty(aldotWorkReport)) {
      return '';
    }

    const { acctId, divId, distId, roadwayClassId, funcId } = aldotWorkReport;
    return `${acctId}-${divId}-${
      isNilOrEmpty(distId) ? '00' : distId
    }-${roadwayClassId}-${funcId}`;
  }, [aldotWorkReport]);

  const notesField = getNotesField(task);
  const showCompletedNotesField =
    notesField && notesField?.value && isCompletedOrAbove;

  const showLocationsSection =
    type === WorkOrderType.TASK_ASSETS_AND_LOCATIONS &&
    (task.id === EMPTY_TASK.id || task?.locationsEnabled);

  const showMultiAssetsSection =
    type === WorkOrderType.TASK_ASSETS_AND_LOCATIONS &&
    (task.id === EMPTY_TASK.id || task?.multiAssetsEnabled);

  const showAssetsSection = type === WorkOrderType.DEFAULT;

  const containerStyle = clsx(classes.taskContainer, lastTask && classes.last);

  return (
    <div className={containerStyle}>
      <div className={classes.taskHeader}>
        {!hideTaskName && (
          <div>
            <div className={classes.taskTitle}>{task?.name}</div>
            {showTitleSubtext && (
              <div className={classes.taskTitleSubtext}>{titleSubtext}</div>
            )}
          </div>
        )}
        {isCompletedOrAbove && (
          <div className={classes.actualCostContainer}>
            Actual Cost:
            <span className={classes.actualCostValue}>
              {numberToLocaleString(task?.actualCost)}
            </span>
          </div>
        )}
      </div>
      {showCustomFields && (
        <CustomFieldsSection
          isCompletedOrAbove={isCompletedOrAbove}
          task={task}
          type={type}
        />
      )}
      <ReportSection
        title="Team"
        isCompletedOrAbove={isCompletedOrAbove}
        actualCost={actualCostData.team}
      >
        <ReportUsers
          users={filteredUsers}
          isCompletedOrAbove={isCompletedOrAbove}
        />
      </ReportSection>
      <ReportSection
        title="Equipment and Material"
        isCompletedOrAbove={isCompletedOrAbove}
        actualCost={actualCostData.material}
      >
        <ReportMaterials
          startEndMaterials={filteredStartEndMaterials}
          usageMaterials={filteredUsageMaterials}
          isCompletedOrAbove={isCompletedOrAbove}
        />
      </ReportSection>
      {showLocationsSection && (
        <ReportSection title="Locations">
          <ReportLocationsSection
            isCompletedOrAbove={isCompletedOrAbove}
            reportLocations={reportLocations}
          />
        </ReportSection>
      )}
      {showMultiAssetsSection && (
        <ReportSection title="Assets">
          <ReportMultiAssetsSection
            isCompletedOrAbove={isCompletedOrAbove}
            reportAssets={reportAssets}
          />
        </ReportSection>
      )}
      {showAssetsSection && (
        <ReportSection title="Assets">
          <ReportAssetsSection reportAssets={reportAssets} />
        </ReportSection>
      )}
      {!isCompletedOrAbove && (
        <ReportSection title="Notes" emptyHeight="4.5rem" />
      )}
      {showCompletedNotesField && (
        <ReportSection title="Notes">
          <div className={classes.basicContainer}>{notesField?.value}</div>
        </ReportSection>
      )}
    </div>
  );
};

export default TaskDetailReport;
